import { Component, ViewChild, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import {
	MatterMortgage,
	MatterMortgageInstructionSource,
	MortgageStep,
} from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { dispatch, Select } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IntakeCancelModalComponent } from './intake-cancel-modal/intake-cancel-modal.component';
import { ICON } from '@core/util/icon';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { User } from '@models/users';
import { GlobalFinancialInstitutionsState } from '@state/global-financial-institutions/global-financial-institutions.state';
import { FinancialInstitution } from '@models/lender';

const CONNECT_STORAGE_KEY = 'hide-sync-promotion';
const INBOX_STORAGE_KEY = 'hide-inbox-promotion';
import { MortgageIntakeFieldsComponent } from './mortgage-intake-fields/mortgage-intake-fields.component';
import { Delete } from 'src/app/shared/state/matter/statementOfAccounts/statementOfAccounts.actions';
import {
	StatementOfAccountDefaultTypes,
	TERAVIEW_FEE_TYPES,
} from '@models/statementOfAccount';

@UntilDestroy()
@Component({
	selector: 'app-mortgage-intake',
	templateUrl: './mortgage-intake.component.html',
	styleUrls: ['./mortgage-intake.component.scss'],
	standalone: false,
})
export class MortgageIntakeComponent implements OnInit {
	public selectedMortgage: MatterMortgage;
	public latestDoc: MatterFile;
	public latestDoc$: Observable<MatterFile>;
	public matterMortgageInstructionSource = MatterMortgageInstructionSource;
	public ICON = ICON;
	public connectStorageKey = CONNECT_STORAGE_KEY;
	public inboxStorageKey = INBOX_STORAGE_KEY;

	public showConnectSyncBanner = false;
	public showUseSyncBanner = false;
	public mortgageDocumentType = MatterDocumentType.MORTGAGE;

	@ViewChild(MortgageIntakeFieldsComponent)
	mortgageIntakeFields: MortgageIntakeFieldsComponent;

	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterUploadedFilesSelectors.getLatestMortgageDocument)
	mortgageDocFilterFn$: Observable<(id: string) => MatterFile>;
	@Select(MatterMortgagesState.getMortgage) mortgageFilterFn$: Observable<
		(id: string) => MatterMortgage
	>;
	@SelectSnapshot(BusinessUsersState.getSyncEnabledLawyers)
	syncEnabledLawyers: User[];
	@SelectSnapshot(GlobalFinancialInstitutionsState.getSyncInstitutions)
	syncInstitutions: FinancialInstitution[];

	private deleteStatementOfAccounts = dispatch(Delete);

	constructor(
		private navService: NavigationService,
		private router: ActivatedRoute,
		private route: NavigationService,
		private matDialog: MatDialog,
		private matterMortgageDispatch: MatterMortgageDispatch,
	) {}

	public onBack(): void {
		this.matDialog
			.open(IntakeCancelModalComponent, {
				maxWidth: '500px',
			})
			.afterClosed()
			.pipe(take(1), untilDestroyed(this))
			.subscribe((result) => {
				if (result) {
					this.matterMortgageDispatch.remove(this.selectedMortgage?.id);
					this.deleteStatementOfAccountsDispatch(
						this.selectedMortgage?.id,
						TERAVIEW_FEE_TYPES,
					);
					this.route.goToMatter_Mortgages(this.matterId);
				}
			});
	}

	private deleteStatementOfAccountsDispatch(
		mortgageId: string,
		types: string[],
	): void {
		types.forEach((type) => {
			// TODO: remove defaultType after the id once script is run
			this.deleteStatementOfAccounts({
				id: `${type}-${mortgageId}-${StatementOfAccountDefaultTypes.REG_MORTGAGE}`,
			});
		});
	}

	continue(): void {
		this.mortgageIntakeFields.triggerValidation();
		if (!this.mortgageIntakeFields.valid) {
			return;
		}

		this.matterMortgageDispatch.updateMortgage(this.selectedMortgage?.id, {
			step: MortgageStep.CREATED,
		});
		this.navService.goToMatter_Mortgages_Mortgage(
			this.matterId,
			this.selectedMortgage.id,
		);
	}

	ngOnInit(): void {
		this.router.params.pipe(untilDestroyed(this)).subscribe((params) => {
			if (params?.mortgageId) {
				this.getMortgage$(params.mortgageId).subscribe((mortgage) => {
					this.selectedMortgage = mortgage;
					const syncLender = Object.values(mortgage?.lenders ?? {}).find(
						(l) => l.closerSyncId,
					);
					if (!syncLender) {
						this.showConnectSyncBanner = false;
						this.showUseSyncBanner = false;
						return;
					}

					if (
						!this.syncEnabledLawyers.length &&
						localStorage.getItem(this.connectStorageKey) !== 'true'
					) {
						this.showConnectSyncBanner = true;
					}

					if (
						this.syncEnabledLawyers.length &&
						localStorage.getItem(`${syncLender.id}-${this.inboxStorageKey}`) !==
							'true'
					) {
						this.showUseSyncBanner = true;
					}
				});
				this.latestDoc$ = this.getMortgageDoc$(params.mortgageId);
				this.latestDoc$.subscribe((doc) => {
					this.latestDoc = doc;
				});
			} else {
				this.selectedMortgage = null;
				this.latestDoc = null;
			}
		});
	}

	getMortgage$(id: string): Observable<MatterMortgage> {
		return this.mortgageFilterFn$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(id)),
		);
	}

	getMortgageDoc$(id: string): Observable<MatterFile> {
		return this.mortgageDocFilterFn$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(id)),
		);
	}

	dismissConnectPromotion() {
		localStorage.setItem(this.connectStorageKey, 'true');
		this.showConnectSyncBanner = false;
	}

	dismissInboxPromotion() {
		const syncLender = Object.values(this.selectedMortgage?.lenders ?? {}).find(
			(l) => l.closerSyncId,
		);

		localStorage.setItem(
			`${syncLender.id ?? ''}-${this.inboxStorageKey}`,
			'true',
		);
		this.showUseSyncBanner = false;
	}

	goToInbox() {
		this.navService.goToMortgageInbox();
	}

	goToCloserSync() {
		this.navService.goToProfile_Integrations_CloserSync();
	}
}
